import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { useTheme } from '@mui/material/styles'
import { FC, useCallback, useEffect, useRef, useState } from 'react'

import { formatTimeForScreenReader, shouldAnnounce } from '../../utils/ScreenReaderUtils'
import NumberBox from './NumberBox'

interface CountdownProps {
  /**
   * `until` is the date and time that the countdown will count down to. It defaults
   * to 24 hours from the current time.
   * @type {Date}
   * @defaultValue new Date(Date.now() + 1000 * 60 * 60 * 24)
   */
  until?: Date

  /**
   * `onCountdownEnd` is a callback function that fires once when the countdown reaches zero.
   * @type {() => void}
   */
  onCountdownEnd?: () => void

  /**
   * `hoursLabel` is the label for the hours countdown.
   * @type {string}
   * @defaultValue ''
   */
  hoursLabel?: string

  /**
   * `minutesLabel` is the label for the minutes countdown.
   * @type {string}
   * @defaultValue ''
   */
  minutesLabel?: string

  /**
   * `secondsLabel` is the label for the seconds countdown.
   * @type {string}
   * @defaultValue ''
   */
  secondsLabel?: string

  /**
   * `showSeconds` is a boolean that determines whether to show the seconds countdown.
   * @type {boolean}
   * @defaultValue false
   */
  showSeconds?: boolean
}

/**
 * `makeEpochMilliseconds` is a utility func that calculates the epoch
 * milliseconds from the current time until the target. If `showSeconds` is
 * false and the time remaining to count down is greater than zero, the func
 * adds 60 seconds to the epoch milliseconds so the count down shows 1 minute.
 * @param until {Date | undefined} - The target datetime to count down to.
 * @param currentTime {number} - The current time in milliseconds.
 * @param showSeconds {undefined | boolean} - True if the countdown should show seconds.
 * @return {number} - The epoch milliseconds from the current time until the target.
 */
const makeEpochMilliseconds = (
  until: Date | undefined,
  currentTime: number,
  showSeconds: undefined | boolean,
): number => {
  if (!until) return 0
  let newEpochMilliseconds = Math.max(until.getTime() - currentTime, 0)
  if (!showSeconds && newEpochMilliseconds > 0) {
    newEpochMilliseconds += 60 * 1000
  }
  return newEpochMilliseconds
}

/**
 * `Countdown` is a React functional component that displays the current hours
 * and minutes from the current time until the target datetime. It optionally
 * displays the seconds if `showSeconds` is set to true.
 *
 * `Countdown` accepts a callback function `onCountdownEnd` that fires once when
 * the countdown reaches zero. If the countdown resumes for any reason, the
 * callback will fire again every time the countdown reaches zero. If the
 * countdown is initialized with a time that has already passed, the callback
 * will fire immediately.
 * @param {CountdownProps} props
 */
const Countdown: FC<CountdownProps> = ({
  until = new Date(Date.now() + 1000 * 60 * 60 * 24),
  onCountdownEnd,
  hoursLabel,
  minutesLabel,
  secondsLabel,
  showSeconds = false,
}: CountdownProps) => {
  const theme = useTheme()

  // refresh every 500ms in case precision matters
  const [currentTime, setCurrentTime] = useState(() => Date.now())
  const [epochMilliseconds, setEpochMilliseconds] = useState(() =>
    makeEpochMilliseconds(until, currentTime, showSeconds),
  )
  const countdownEnded = useRef(false)

  const hoursDigits = Math.floor(epochMilliseconds / (1000 * 60 * 60))
  const minutesDigits = Math.floor(epochMilliseconds / (1000 * 60)) % 60
  const secondsDigits = Math.ceil(epochMilliseconds / 1000) % 60

  // the interval for announcing countdown to screen readers (WCAG compliance)
  const [lastAnnouncement, setLastAnnouncement] = useState(0)
  const announceInterval = 30 // seconds

  const triggerScreenReaderAnnouncement = useCallback(() => {
    setLastAnnouncement(Math.floor(epochMilliseconds / 1000))
    return formatTimeForScreenReader(hoursDigits, minutesDigits)
  }, [epochMilliseconds])

  // update epoch milliseconds whenever the countdown target, current time, or the
  // option to show seconds changes
  useEffect(() => {
    setEpochMilliseconds(makeEpochMilliseconds(until, currentTime, showSeconds))
  }, [until, currentTime, showSeconds])

  // update the current time every 500ms, firing the countdown end callback upon
  // reaching zero
  useEffect(() => {
    if (epochMilliseconds <= 0 && !countdownEnded.current) {
      countdownEnded.current = true
      if (onCountdownEnd) {
        onCountdownEnd()
      }
      return
    }

    if (epochMilliseconds > 0) {
      countdownEnded.current = false
    }

    const interval = setInterval(() => {
      setCurrentTime(Date.now())
    }, 500)

    return () => clearInterval(interval)
  }, [epochMilliseconds])

  const colonSeparator = (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        width: '1.25rem',
        pt: '0.5rem',
      }}
    >
      <Typography
        variant="h3"
        component="span"
        sx={{
          fontSize: '2.9375rem',
          [theme.breakpoints.down('lg')]: {
            fontSize: '2.9375rem',
          },
        }}
      >
        :
      </Typography>
    </Box>
  )

  return (
    <Box
      role="timer"
      aria-label="Time remaining until another request can be made"
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'start',
        justifyContent: 'center',
      }}
    >
      <NumberBox num={hoursDigits} label={hoursLabel} />
      {colonSeparator}
      <NumberBox num={minutesDigits} label={minutesLabel} />
      {showSeconds && (
        <>
          {colonSeparator}
          <NumberBox num={secondsDigits} label={secondsLabel} />
        </>
      )}
      {/* Announce countdown for screenreaders */}
      <Box
        sx={{
          position: 'absolute',
          left: '-10000px',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
        }}
        aria-live="polite"
        aria-atomic="true"
      >
        {shouldAnnounce(announceInterval, lastAnnouncement, epochMilliseconds)
          ? triggerScreenReaderAnnouncement()
          : ''}
      </Box>
    </Box>
  )
}

export type { CountdownProps }

export default Countdown
